import {
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  from,
  fromPromise,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';

import { inspectorAuthInteractor } from 'client/amplify/auth';

const generateAuthToken = async () => {
  const token = await inspectorAuthInteractor.getAuthToken();

  if (!token) return '';

  const decoded = JSON.parse(
    Buffer.from(token.split('.')[1], 'base64').toString('utf-8'),
  );

  const mockToken = `${decoded.sub}:${decoded['cognito:username']}`;

  return `Bearer ${
    process.env.NEXT_PUBLIC_USE_MOCK_TOKEN === 'true' ? mockToken : token
  }`;
};

const httpLink = createHttpLink({
  uri: process.env.NEXT_PUBLIC_API_ENDPOINT || 'http://localhost:8080/graphql',
});

const authLink = setContext(async (_, { headers }) => {
  const authorization = await generateAuthToken();

  return {
    headers: {
      ...headers,
      authorization,
    },
  };
});

// ネットワークエラー時、10秒毎に最大10回リトライ
const MAX_RETRY_COUNT = 10;
const retryLink = new RetryLink({
  attempts: (count, operation, error) => {
    if (error) {
      console.log('networkError', error);
    }
    if (operation.operationName === 'ConnectionCheck') {
      return false;
    }
    const shouldRetry = !!error && count < MAX_RETRY_COUNT;
    return shouldRetry;
  },
  delay: {
    initial: 10000,
    max: 10000,
    jitter: false,
  },
});
// 認証エラー時、認証トークンを更新してリトライ
const errorLink = onError(({ graphQLErrors, operation, forward }) => {
  if (graphQLErrors) {
    console.log('graphQLErrors', graphQLErrors);
    const authError = graphQLErrors.find(
      (error) =>
        error.extensions?.code === 'UNAUTHENTICATED' ||
        error.message.toLowerCase().includes('unauthorized'),
    );
    if (authError) {
      return fromPromise(
        generateAuthToken().then((authorization) => {
          operation.setContext(({ headers = {} }) => ({
            headers: {
              ...headers,
              authorization,
            },
          }));
        }),
      ).flatMap(() => forward(operation));
    }
  }
});

export const client = new ApolloClient({
  link: authLink.concat(from([errorLink, retryLink, httpLink])),
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
    },
  },
});
